import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, X, Clock, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useCompanySettings, useUpdateCompanySettings } from '@/api/queries/settings.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';

const DEFAULTS = [
  { id: 's-001', name: 'Morning Shift', start_time: '09:00', end_time: '18:00', color: '#007AFF' },
  { id: 's-002', name: 'Evening Shift', start_time: '14:00', end_time: '23:00', color: '#FF9500' },
  { id: 's-003', name: 'Night Shift', start_time: '22:00', end_time: '07:00', color: '#AF52DE' },
];

const COLORS = ['#007AFF','#34C759','#FF9500','#FF3B30','#AF52DE','#5AC8FA'];

function ShiftModal({ shift, onSave, onClose }) {
  const [form, setForm] = useState(shift || { name: '', start_time: '09:00', end_time: '18:00', color: '#007AFF' });
  const inputCls = "w-full h-10 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[var(--bg-card)] rounded-[20px] w-full max-w-sm border border-[var(--border)] shadow-[var(--shadow-xl)] fade-in">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--border)]">
          <h2 className="text-[18px] font-bold text-[var(--text-primary)]">{shift ? 'Edit Shift' : 'New Shift'}</h2>
          <button onClick={onClose} className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)]"><X size={15}/></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[var(--text-primary)]">Shift Name</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Morning Shift" className={inputCls}/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[['Start Time','start_time'],['End Time','end_time']].map(([label, key]) => (
              <div key={key} className="space-y-1.5">
                <label className="text-[13px] font-semibold text-[var(--text-primary)]">{label}</label>
                <input type="time" value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className={inputCls}/>
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[var(--text-primary)]">Color</label>
            <div className="flex gap-2">
              {COLORS.map(c => (
                <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                  style={{ backgroundColor: c }}
                  className={`w-7 h-7 rounded-full transition-all ${form.color === c ? 'ring-2 ring-offset-2 ring-[var(--accent)]' : ''}`}/>
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button className="flex-1" onClick={() => { if (form.name.trim()) { onSave(form); onClose(); } }}>
              {shift ? 'Update Shift' : 'Add Shift'}
            </Button>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function calcDuration(start, end) {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  const h = Math.floor(mins / 60), m = mins % 60;
  return `${h}h${m > 0 ? ` ${m}m` : ''}`;
}

export default function ShiftsSettingsPage() {
  const { data } = useCompanySettings();
  const { mutate, isPending } = useUpdateCompanySettings();
  const [shifts, setShifts] = useState(DEFAULTS);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    const saved = data?.data?.settings?.shifts;
    if (saved?.length) setShifts(saved);
  }, [data]);

  const saveModal = (form) => {
    if (modal.shift) {
      setShifts(ss => ss.map(s => s.id === modal.shift.id ? { ...modal.shift, ...form } : s));
    } else {
      setShifts(ss => [...ss, { ...form, id: `s-${Date.now()}` }]);
    }
  };

  const handleSave = (updatedShifts) => {
    const toSave = updatedShifts || shifts;
    mutate({ settings: { shifts: toSave } }, {
      onSuccess: () => toast.success('Shifts saved'),
      onError: () => toast.error('Failed to save'),
    });
  };

  return (
    <div className="space-y-5 fade-in max-w-xl">
      <PageHeader
        title="Shift Configuration"
        subtitle={`${shifts.length} shifts defined`}
        breadcrumb={[{ label: 'Settings', href: '/settings' }, { label: 'Shifts' }]}
        actions={<Button size="sm" icon={Plus} onClick={() => setModal({ shift: null })}>New Shift</Button>}
      />
      <div className="space-y-3">
        {shifts.map(s => (
          <div key={s.id} className="card flex items-center justify-between p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-[10px] flex items-center justify-center" style={{ backgroundColor: s.color + '20' }}>
                <Clock size={16} style={{ color: s.color }}/>
              </div>
              <div>
                <p className="text-[14px] font-semibold text-[var(--text-primary)]">{s.name}</p>
                <p className="text-[12px] text-[var(--text-tertiary)]">{s.start_time} – {s.end_time} · {calcDuration(s.start_time, s.end_time)}</p>
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => setModal({ shift: s })} className="p-2 rounded-[6px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]"><Pencil size={14}/></button>
              <button onClick={() => setShifts(ss => ss.filter(x => x.id !== s.id))} className="p-2 rounded-[6px] hover:bg-[var(--danger)]/10 text-[var(--text-tertiary)] hover:text-[var(--danger)]"><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
      </div>
      <Button icon={Save} onClick={() => handleSave()} disabled={isPending}>{isPending ? 'Saving…' : 'Save Shifts'}</Button>
      {modal !== null && <ShiftModal shift={modal.shift} onSave={saveModal} onClose={() => setModal(null)}/>}
    </div>
  );
}
